/**
 * Oppie PDP video-rij (blocks/oppie-pdp-ugc-videos.liquid).
 * Tegel aanklikken opent de video groot in een stories-venster met
 * voortgangsbalkjes, pauze, geluid, vorige/volgende en swipen. Aan het eind
 * van een video gaat hij door naar de volgende. Tegels die in beeld zijn spelen
 * stil af (zoals Safecourt), buiten beeld en tijdens de popup pauzeren ze.
 */

const SWIPE_MIN_PX = 50;
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class OppieUgcVideos extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-ugc-dialog]');
    this.video = this.querySelector('[data-ugc-video]');
    this.thumbs = [...this.querySelectorAll('[data-ugc-open]')];
    this.bars = [...this.querySelectorAll('[data-ugc-bar]')];
    if (!this.dialog || !this.video || this.thumbs.length === 0) return;

    this.index = 0;
    this.loadId = 0;
    this.muted = false;
    this.toggleButton = this.querySelector('[data-ugc-toggle]');
    this.soundButton = this.querySelector('[data-ugc-sound]');
    this.touchStartX = null;

    this.bindThumbs();
    this.observeThumbs();
    this.bindDialog();
    this.bindVideo();
  }

  bindThumbs() {
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.open(i));
    });
  }

  observeThumbs() {
    this.visibleThumbs = new Set();
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) this.visibleThumbs.add(target);
        else this.visibleThumbs.delete(target);
        if (isIntersecting && !this.dialog.open) this.startPreview(target);
        else this.stopPreview(target);
      });
    }, { threshold: 0.5 });
    this.thumbs.forEach((thumb) => this.observer.observe(thumb));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  bindDialog() {
    const on = (selector, handler) => this.querySelector(selector)?.addEventListener('click', handler);
    on('[data-ugc-close]', () => this.dialog.close());
    on('[data-ugc-toggle]', () => this.togglePlay());
    on('[data-ugc-sound]', () => this.toggleSound());
    on('[data-ugc-prev]', () => this.go(this.index - 1));
    on('[data-ugc-next]', () => this.go(this.index + 1));

    this.dialog.addEventListener('click', (event) => {
      const outsideFrame = !event.target.closest('[data-ugc-frame]');
      if (outsideFrame) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => this.teardown());
    this.dialog.addEventListener('keydown', (event) => this.onKey(event));
    this.dialog.addEventListener('touchstart', (event) => {
      this.touchStartX = event.changedTouches[0].clientX;
    }, { passive: true });
    this.dialog.addEventListener('touchend', (event) => this.onSwipe(event), { passive: true });
  }

  bindVideo() {
    const video = this.video;
    video.addEventListener('click', () => this.togglePlay());
    video.addEventListener('play', () => this.setPaused(false));
    video.addEventListener('pause', () => this.setPaused(true));
    video.addEventListener('timeupdate', () => this.renderProgress());
    video.addEventListener('ended', () => this.go(this.index + 1));
  }

  open(index) {
    this.dialog.showModal();
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.thumbs.forEach((thumb) => this.stopPreview(thumb));
    this.load(index);
  }

  teardown() {
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    document.documentElement.style.overflow = this.previousOverflow ?? '';
    this.visibleThumbs?.forEach((thumb) => this.startPreview(thumb));
  }

  go(index) {
    const total = this.thumbs.length;
    this.load((index + total) % total);
  }

  load(index) {
    const thumb = this.thumbs[index];
    if (!thumb) return;
    this.index = index;
    this.loadId += 1;
    this.video.poster = thumb.dataset.poster || '';
    this.video.src = thumb.dataset.src;
    this.video.muted = this.muted;
    this.renderProgress();
    this.play();
  }

  play() {
    const loadId = this.loadId;
    const isStale = (error) => loadId !== this.loadId || error?.name === 'AbortError';
    this.video.play().catch((error) => {
      if (isStale(error)) return;
      if (error?.name !== 'NotAllowedError' || this.video.muted) {
        this.setPaused(true);
        return;
      }
      // Browser weigert geluid zonder gebaar: start stil, knop toont dat.
      this.setMuted(true);
      this.video.play().catch((retryError) => {
        if (!isStale(retryError)) this.setPaused(true);
      });
    });
  }

  setPaused(paused) {
    this.dialog.toggleAttribute('data-paused', paused);
    this.toggleButton?.setAttribute('aria-pressed', String(!paused));
  }

  togglePlay() {
    if (this.video.paused) this.play();
    else this.video.pause();
  }

  toggleSound() {
    this.setMuted(!this.muted);
  }

  setMuted(muted) {
    this.muted = muted;
    this.video.muted = muted;
    this.dialog.toggleAttribute('data-muted', muted);
    this.soundButton?.setAttribute('aria-pressed', String(!muted));
  }

  renderProgress() {
    const { currentTime, duration } = this.video;
    const current = duration ? (currentTime / duration) * 100 : 0;
    this.bars.forEach((bar, i) => {
      const width = i < this.index ? 100 : i === this.index ? current : 0;
      bar.style.width = `${width}%`;
    });
  }

  onKey(event) {
    if (event.key === 'ArrowLeft') this.go(this.index - 1);
    if (event.key === 'ArrowRight') this.go(this.index + 1);
  }

  onSwipe(event) {
    if (this.touchStartX === null) return;
    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    this.touchStartX = null;
    if (Math.abs(deltaX) < SWIPE_MIN_PX || this.thumbs.length < 2) return;
    this.go(deltaX < 0 ? this.index + 1 : this.index - 1);
  }

  startPreview(thumb) {
    if (REDUCED_MOTION.matches || !thumb.dataset.preview) return;
    let preview = thumb.querySelector('.oppie-ugc__preview');
    if (!preview) {
      preview = document.createElement('video');
      preview.className = 'oppie-ugc__preview';
      Object.assign(preview, { muted: true, loop: true, playsInline: true, src: thumb.dataset.preview });
      // iOS start alleen stil inline als muted/playsinline ook als attribuut staan.
      preview.setAttribute('muted', '');
      preview.setAttribute('playsinline', '');
      preview.setAttribute('aria-hidden', 'true');
      const poster = thumb.querySelector('.oppie-ugc__poster');
      if (poster) poster.after(preview);
      else thumb.prepend(preview);
    }
    preview.play()
      .then(() => thumb.setAttribute('data-previewing', ''))
      .catch(() => thumb.removeAttribute('data-previewing'));
  }

  stopPreview(thumb) {
    thumb.removeAttribute('data-previewing');
    thumb.querySelector('.oppie-ugc__preview')?.pause();
  }
}

if (!customElements.get('oppie-ugc-videos')) {
  customElements.define('oppie-ugc-videos', OppieUgcVideos);
}
